import axios from "axios";
import { deleteCookie, getCookie, setCookie } from "../utils/cookieUtils";
import { result } from "lodash";
import * as Sentry from "@sentry/react"

// Request deduplication cache
const requestCache = new Map();
const REQUEST_CACHE_TTL = 3000; // Reduced to 3 seconds for better responsivenes


const getPartitionedKey = (key) => {
  if (window.Office && Office.context && Office.context.partitionKey) {
    return `${Office.context.partitionKey}_${key}`;
  }
  return key;
};

const getPartitionedLocalStorage = (key) => {
  return localStorage.getItem(getPartitionedKey(key));
};

//test comment
const apiService = {
  async downloadAudio(payload) {
    // Payload should be { ai_response_id: number, fileType: 'WAV' | 'MP3' }
    const method = 'POST';
    const endpoint = '/download-audio';
    const token = getCookie("token") || getPartitionedLocalStorage("token");
    try {
        console.log(`[apiService] Requesting download: ${method} ${endpoint}`, payload);
        const response = await axios({
            method: method,
            url: `${process.env.REACT_APP_SERVER_ENDPOINT}/api${endpoint}`, // Adjust base URL if needed
            data: payload,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            // --- IMPORTANT: Expect a file blob, not JSON ---
            responseType: 'blob',
            // ---
        });
        console.log(`[apiService] Download response received (status ${response.status})`);
        // Return the whole response so the caller can get headers and blob data
        return response;
    } catch (error) {
        console.error(`[apiService] Error calling ${method} ${endpoint}:`, error.response?.data || error.message);
        Sentry.captureException(error);
        // Try to parse error response if it's a blob containing JSON error info
        if (error.response && error.response.data instanceof Blob && error.response.data.type === 'application/json') {
            const errorJson = await error.response.data.text();
            try {
                const parsedError = JSON.parse(errorJson);
                console.error("[apiService] Parsed download error details:", parsedError);
                throw new Error(parsedError.message || `Failed to download audio (status ${error.response.status})`);
            } catch (parseError) {
                 console.error("[apiService] Failed to parse blob error response:", parseError);
                 Sentry.captureException(parseError);
            }
        }
        // Throw a generic or specific error
        throw new Error(error.response?.data?.message || `Failed to download audio`);
    }
  },
  async updateSelectedTrack(payload) {
    // Use PATCH method as defined in your routes.js
    const method = 'PATCH';
    const endpoint = '/select-track';
    const token = getCookie("token") || getPartitionedLocalStorage("token");
    try {
        const response = await axios({
            method: method,
            url: `${process.env.REACT_APP_SERVER_ENDPOINT}/api${endpoint}`, // Adjust base URL if needed
            data: payload,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        console.log(`[apiService] ${method} ${endpoint} success:`, response.data);
        return response.data; // Return data on success
    } catch (error) {
        console.error(`[apiService] Error calling ${method} ${endpoint}:`, error.response?.data || error.message);
        Sentry.captureException(error);
        // Throw a structured error or the original error
        throw new Error(error.response?.data?.message || `Failed to ${method} ${endpoint}`);
    }
  },


  sendRequest: async (taskName, payload) => {

    // console.log(`[API] Send request: ${taskName}`, payload);
    //DUMMY PUSH TO TRIGGER GITHUB ACTIONS
    // console.log("DUMMY PUSH TO TRIGGER GITHUB ACTIONS 2");

    // Handle advanced audio editing endpoints with the new approach
    if (taskName === 'advancedAudioEdit') {
      console.log('[API] Using direct endpoint for advancedAudioEdit');
      return apiService.handleAdvancedAudioEdit(payload);
    }

    if (taskName === 'getLatestProjectTrack') {
      console.log('[API] Using direct endpoint for getLatestProjectTrack');
      return apiService.getLatestProjectTrack(payload.project_id, payload.user_id);
    }

    
    const requestData = {
      taskName: taskName,
      payload: payload,
      endpoint: `${process.env.REACT_APP_SERVER_ENDPOINT}/api/execute`,
      method: "POST",
    };

    try {
      const response = await axios.post(requestData.endpoint, requestData);

      //console.log("Task Key : ", taskName);
      //console.log("Response Data : ", response.data);

      // if (response.data.success == "Profile created") {
      //   // console.log({
      //   //   success: response.data.success,
      //   //   ...response.data.user.rows[0],
      //   // });
        
      //   // return {
      //   //   success: response.data.success,
      //   //   ...response.data.user.rows[0], // directly flatten the user row
      //   // };

        // const responseData = {
          // success: response.data.success,
          // user: response.data.user.rows[0]
        // };
        
        // return responseData; 
      // }

      return response.data;
    } catch (error) {
      console.error("Error executing query:", error);
      Sentry.captureException(error);
      // If error response exists and contains a backend error message
      if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error); // Custom error shown to UI
      }
    
      // Fallback generic error
      throw new Error("An unexpected error occurred. Please try again.");
    }
  },

  updateAIResponseLike: async (payload) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");

    if (!token) {
      console.error("Authentication token not found.");
      return { error: "Authentication token not found" };
    }

    try {
      // Add a flag to indicate when we're disliking content (for refund processing)
      // This is the important part - ensure we tell the backend this is a dislike action
      const enhancedPayload = {
        ...payload,
        process_refund: payload.dislike_reason && !payload.is_like
      };

      // Use the enhanced payload with the process_refund flag
      const result = await apiService.handleProtectedRoute("updateAIResponseLike", enhancedPayload);

      console.log("AI response like updated successfully:", result);
      return result;
    } catch (error) {
      console.error(
        "Error updating AI response like:",
        error.response?.data || error.message
      );
      Sentry.captureException(error);
      throw error;
    }
  },

  submitProfile: async (profileData) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/submitProfile`,
        profileData
      );
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      // console.log(response.data);
      return response.data;
    } catch (error) {
      console.error("Error submitting profile:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  CreateTeamSubmission: async (profileData) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/create-team-checkout-session`,
        profileData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      // console.log(response.data);
      return response.data;
    } catch (error) {
      console.error("Error CreateTeamSubmission profile:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  UpgradeMemberCheckout: async (profileData) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/upgrade-member-checkout-session`,
        profileData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      return response.data;
    } catch (error) {
      console.error("Error upgrade member checkout session:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  requestPasswordReset: async (email) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/requestPasswordReset`,
        { email }
      );
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      // console.log(response.data);
      return response.data;
    } catch (error) {
      console.error("Error submitting profile:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  resetPassword: async ({ password, token }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/resetPassword`,
        { password, token }
      );
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      return response.data;
    } catch (error) {
      console.error("Error submitting profile:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  // function for handling protected routes with taskName
  handleProtectedRoute: async (taskName, payload) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token"); // Get the token from cookies after user sign in

    if (!token) {
      throw new Error("No token found. Authorization required.");
    }

    // Request deduplication for critical endpoints
    const cacheKey = `${taskName}_${JSON.stringify(payload)}`;
    const now = Date.now();
    
    // Check if we have a recent request for the same data
    if (requestCache.has(cacheKey)) {
      const { data, timestamp } = requestCache.get(cacheKey);
      if (now - timestamp < REQUEST_CACHE_TTL) {
        console.log(`[CACHE HIT] Using cached response for ${taskName}`);
        return data;
      } else {
        requestCache.delete(cacheKey);
      }
    }

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/protected`;
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    // Add user_id to payload if token exists
    const user_id = getCookie("user_id") || getPartitionedLocalStorage("user_id");
    if (user_id) {
      payload.user_id = user_id;
    }

    const requestData = {
      taskName: taskName,
      payload: payload,
    };

    try {
      const response = await axios.post(endpoint, requestData, { headers });
      console.log( "taskName:" ,taskName,"\nResponse From Protected : " , response.data);
      
      // Cache the response for critical endpoints
      if (['fetchProjectsWithLastAiResponse', 'fetchProjectsCount', 'fetchUserProfile', 'fetchUserNameAndAvatar', 'fetchTeamNameAndAvatar', 'fetchNewNotifications'].includes(taskName)) {
        requestCache.set(cacheKey, { data: response.data, timestamp: now });
        
        // Clean up old cache entries
        setTimeout(() => {
          if (requestCache.has(cacheKey)) {
            const { timestamp: cachedTimestamp } = requestCache.get(cacheKey);
            if (now - cachedTimestamp >= REQUEST_CACHE_TTL) {
              requestCache.delete(cacheKey);
            }
          }
        }, REQUEST_CACHE_TTL);
      }
      
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        // Handle 401 Unauthorized error
        deleteCookie("token");
        deleteCookie("user_id");

        window.location.href = "/"; // Redirect to sign-in page
      }

      console.error("Error executing protected route request:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  updateAvatar: async (formData) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");

    if (!token) {
      throw new Error("No token found. Authorization required.");
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/updateAvatar`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Include the token in the Authorization header
            "Content-Type": "multipart/form-data", // Ensure the content type is correct for file uploads
          },
        }
      );
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      // console.log(response.data);
      return response.data;
    } catch (error) {
      console.error("Error updating avatar:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  storeUserMessage: async (formData) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");

    if (!token) {
      throw new Error("No token found. Authorization required.");
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/storeUserMessage`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Include the token in the Authorization header
            "Content-Type": "multipart/form-data", // Ensure the content type is correct for file uploads
          },
        }
      );
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      // console.log(response.data);
      return response.data;
    } catch (error) {
      console.error("Error updating avatar:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  changeAudioCoverImage: async (formData) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");

    if (!token) {
      throw new Error("No token found. Authorization required.");
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/changeAudioCoverImage`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      // console.log(response.data);
      return response.data;
    } catch (error) {
      console.error("Error updating audio description:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  createTeamApplication: async (payload) => {
    const token = getCookie("token");

    if (!token) {
      throw new Error("No token found. Authorization required.");
    }
    const formData = new FormData();
    formData.append("name", payload.name);
    formData.append("description", payload.description);
    if (payload.logo) {
      formData.append("logo", payload.logo);
    }
    formData.append("memberEmails", JSON.stringify(payload.memberEmails));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/createTeamApplication`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating organization:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  createTeam: async (payload) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");

    if (!token) {
      throw new Error("No token found. Authorization required.");
    }
    const formData = new FormData();
    formData.append("name", payload.name);
    formData.append("description", payload.description);
    if (payload.logo) {
      formData.append("logo", payload.logo);
    }
    formData.append("memberEmails", JSON.stringify(payload.memberEmails));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/createOrganization`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating organization:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  updateTeamLogo: async (formData) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");

    if (!token) {
      throw new Error("No token found. Authorization required.");
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/updateTeamLogo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      return response.data;
    } catch (error) {
      console.error("Error updating team logo:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  sendInvitation: async (team_id, email) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");

    if (!token) {
      // Return an error object consistent with the new structure
      console.error("No token found. Authorization required.");
      return { success: false, error: "No token found. Authorization required.", status: 401 };
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/sendInvitation`,
        { team_id, email },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Return success structure
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error sending invitation:", error);
      Sentry.captureException(error);
      // Check if it's an Axios error and specifically a 500
      if (axios.isAxiosError(error) && error.response?.status === 500) {
        // Log the server error but return a specific error structure for the UI
        console.error("Server returned 500 during invitation. The invitation might have been sent. Check server logs.");
        return {
          success: false,
          error: "An internal server error occurred while sending the invitation. Please check if the user was added or try again later.",
          status: 500,
          // Optionally include original error data if needed by the UI
          // errorDetails: error.response?.data 
        };
      }
      // For other errors, re-throw or return a generic error structure
      return {
        success: false,
        error: error.message || "An unknown error occurred",
        status: error.response?.status
      };
      // Alternatively, you might want to re-throw for unexpected errors:
      // throw error; 
    }
  },

  resendInvitation: async (team_id, email, paidFor) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");

    if (!token) {
      throw new Error("No token found. Authorization required.");
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/resendInvitation`,
        { team_id, email, paidFor },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error sending invitation:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  deleteAccount: async (reason) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");

    if (!token) {
      throw new Error("No token found. Authorization required.");
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/deleteAccount`,
        { reason },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting account:", error);
      Sentry.captureException(error);
      throw error;
    }
  },

  createShareLink: async (response_id) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");
    if (!token) {
      throw new Error("No token found. Authorization required.");
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/create-share-link`,
        { response_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data; // Should contain { success: true, shareUrl: '...' }
    } catch (error) {
      console.error("Error creating share link:", error);
      Sentry.captureException(error);
      throw error.response?.data || error;
    }
  },

  getSharedTrack: async (share_id) => {
    // This is a public endpoint, no token needed
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/shared-track/${share_id}`
      );
      return response.data; // Should contain { success: true, sharedTrack: {...} }
    } catch (error) {
      console.error("Error fetching shared track:", error);
      Sentry.captureException(error);
      throw error.response?.data || error;
    }
  },

  getSimilarTracks: async (tagsPayload) => {
    // tagsPayload should be like { theme: "Pop", mood: "happy", genre: "soundtrack", instrument: "piano", excludeId: (optional) id_to_exclude }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/similar-shared-tracks`,
        tagsPayload
      );
      return response.data; // Should contain { success: true, similarTracks: [...] }
    } catch (error) {
      console.error("Error fetching similar tracks:", error);
      Sentry.captureException(error);
      throw error.response?.data || error;
    }
  },

  // Define API endpoints
  endpoints: {
    // ... (other existing endpoints if any) ...

    // Advanced Audio Editing Endpoints
    getLatestProjectTrack: {
      method: 'GET',
      url: '/api/latest-project-track', // Ensure this path matches your backend route
      requiresAuth: true
    },

    advancedAudioEdit: {
      method: 'POST',
      url: '/api/advanced-audio-edit', // Ensure this path matches your backend route
      requiresAuth: true
    },

    getProjectAudioEdits: {
      method: 'GET',
      url: '/api/project-audio-edits', // Ensure this path matches your backend route
      requiresAuth: true
    },
  },

  callEndpoint: async function (endpointName, data) {
    console.log(`[API] Calling endpoint: ${endpointName}`, data);

    // When sending advanced audio edit, log detailed payload information
    if (endpointName === 'advancedAudioEdit') {
      console.log('[API DEBUG] ===== Advanced Audio Edit Request Payload =====');
      console.log(`[API DEBUG] Prompt: "${data.prompt}"`);
      console.log('[API DEBUG] Edit operations:');
      console.table(data.edit_operations);
      console.log(`[API DEBUG] Original Audio ID: ${data.original_audio_id}`);
      console.log(`[API DEBUG] Audio URI: ${data.audioUri}`);
      console.log('[API DEBUG] ============================================');
    }

    const endpoint = this.endpoints[endpointName];
    if (!endpoint) {
      console.error(`[API] Endpoint not found: ${endpointName}`);
      throw new Error(`Unknown endpoint: ${endpointName}`);
    }

    const url = `${process.env.REACT_APP_SERVER_ENDPOINT}${endpoint.url}`;
    const method = endpoint.method;
    const headers = {};

    if (endpoint.requiresAuth) {
      const token = getCookie('token');
      if (!token) {
        console.error(`[API] No token found for authenticated endpoint: ${endpointName}`);
        throw new Error('Authentication required');
      }
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      console.log(`[API] Making ${method} request to ${url}`);
      let response;

      // Add a unique request ID to track this specific request
      const requestId = `req_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 5)}`;
      console.log(`[API DEBUG] Request ID: ${requestId} - Starting request`);

      if (method === 'GET') {
        response = await axios.get(url, { params: data, headers });
      } else {
        // Clone the data before sending to ensure we can see the exact data being sent
        const payload = JSON.parse(JSON.stringify(data));
        console.log(`[API DEBUG] Request ID: ${requestId} - Sending exact payload:`, payload);

        // Add additional headers for debugging if needed
        const debugHeaders = { ...headers, 'X-Request-ID': requestId };
        response = await axios.post(url, payload, { headers: debugHeaders });
      }

      console.log(`[API DEBUG] Request ID: ${requestId} - Response received`);
      console.log(`[API] ${endpointName} response:`, response.data);

      // // For advanced audio edit, check the response
      // if (endpointName === 'advancedAudioEdit' && response.data) {
      //   console.log('[API DEBUG] ===== Advanced Audio Edit Response =====');
      //   console.log(`[API DEBUG] Success: ${response.data.success}`);
      //   console.log(`[API DEBUG] Edited Audio URL: ${response.data.editedAudioUrl}`);
      //   console.log(`[API DEBUG] Original Audio ID: ${response.data.originalAudioId}`);
      //   console.log('[API DEBUG] ============================================');
      // }

      return response.data;
    } catch (error) {
      console.error(`[API] Error calling ${endpointName}:`, error);
      Sentry.captureException(error);
      if (error.response) {
        console.error(`[API] Response error data:`, error.response.data);
        throw error.response.data;
      }
      throw error;
    }
  },
  markAsReadNotifications: async () => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");
    // console.log("token ", token);

    if (!token) {
      console.error("token not provided");
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/markAsReadNotifications`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error reading notifications:", error);
      Sentry.captureException(error);
      throw error;
    }
  },
  handleAdvancedAudioEdit: async function (payload) {
    // console.log("[API] Handling advanced audio edit with payload", payload);

    // Log payload details before any potential transformations
    // console.log('[API DEBUG] ===== handleAdvancedAudioEdit - Original Payload =====');
    // console.log(`[API DEBUG] Prompt from frontend: "${payload.prompt}"`);
    // console.log('[API DEBUG] Edit operations from frontend:');

    // Log each edit operation for clarity
    if (payload.edit_operations && Array.isArray(payload.edit_operations)) {
      payload.edit_operations.forEach((op, index) => {
        console.log(`[API DEBUG] Operation ${index + 1}:`, op);
      });
    }

    // console.log(`[API DEBUG] Audio URI: ${payload.audioUri}`);
    // console.log(`[API DEBUG] Project ID: ${payload.project_id}`);
    // console.log(`[API DEBUG] User ID: ${payload.user_id}`);
    // console.log(`[API DEBUG] Original Audio ID: ${payload.original_audio_id}`);
    // console.log('[API DEBUG] ==================================================');

    // Check for any existing callback data or cached edits that might interfere
    // console.log('[API DEBUG] Checking for cached edit operations or previous edits...');

    // Compare with any previously saved edit operations from other requests
    const sessionStorageKey = `last_edit_${payload.project_id}_${payload.user_id}`;
    const lastEdit = sessionStorage.getItem(sessionStorageKey);
    if (lastEdit) {
      // console.log('[API DEBUG] Found previous edit in session storage:', lastEdit);
    } else {
      // console.log('[API DEBUG] No previous edit found in session storage');
    }

    // Create a deep clone of the payload to debug any mutation issues
    const clonedPayload = JSON.parse(JSON.stringify(payload));
    console.log('[API DEBUG] Cloned payload to prevent mutations:',
      clonedPayload.prompt === payload.prompt ? 'Prompt matches original' : 'Prompt differs from original');

    // Save the current edit operations for future reference
    try {
      sessionStorage.setItem(sessionStorageKey, JSON.stringify({
        prompt: payload.prompt,
        operations_count: payload.edit_operations?.length || 0,
        timestamp: new Date().toISOString()
      }));
      // console.log('[API DEBUG] Saved current edit to session storage');
    } catch (err) {
      // console.log('[API DEBUG] Could not save to session storage:', err.message);
      Sentry.captureException(err);
    }

    // Now call the endpoint with the payload
    return this.callEndpoint('advancedAudioEdit', payload);
  },

  // Add method to get latest project track
  getLatestProjectTrack: async function (projectId, userId) {
    // console.log(`[API] Getting latest track for project ${projectId}, user ${userId}`);
    return this.callEndpoint('getLatestProjectTrack', { project_id: projectId, user_id: userId });
  },

  // Add method to get audio edits by original audio ID
  getProjectAudioEdits: async function (projectId, userId, originalAudioId) {
    // console.log(`[API] Getting audio edits for project ${projectId}, user ${userId}, original audio ID ${originalAudioId}`);
    return this.callEndpoint('getProjectAudioEdits', {
      project_id: projectId,
      user_id: userId,
      original_audio_id: originalAudioId
    });
  },
  downloadAudio: async (payload) => {
    // Destructure the expected properties from the payload
    const { ai_response_id, fileType = 'WAV', trackId, songTitle = '' } = payload;

    // console.log('[apiService] Starting downloadAudio (v2 - object payload):', {
    //   ai_response_id,
    //   fileType,
    //   trackId,
    //   songTitle
    // });

    if (ai_response_id === undefined || trackId === undefined) {
      console.error('[apiService] Missing ai_response_id or trackId in download payload', payload);
      throw new Error("Missing required information (response ID or track ID) for download.");
    }

    const token = getCookie("token") || getPartitionedLocalStorage("token");

    if (!token) {
      console.error('[apiService] No token found for download');
      throw new Error("No token found. Authorization required.");
    }

    try {
      // 1. Fetch GCS URI directly using the response ID and track ID
      // console.log(`[apiService] Fetching GCS URI for response: ${ai_response_id}, track: ${trackId}`);
      const gcsUriEndpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/ai-response-gcs-uri/${ai_response_id}/${trackId}`;
      const gcsResponse = await fetch(
        gcsUriEndpoint,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const gcsData = await gcsResponse.json();

      console.log('[apiService] GCS URI Response:', {
        status: gcsResponse.status,
        statusText: gcsResponse.statusText,
        data: gcsData
      });

      if (!gcsData.success || !gcsData.gcsUri) {
        console.error('[apiService] GCS URI Extraction Failed:', {
          success: gcsData.success,
          gcsUri: gcsData.gcsUri,
          message: gcsData.message,
          error: gcsData.error
        });
        throw new Error(gcsData.message || "Could not find the audio file URI needed for download.");
      }

      const gcsUri = gcsData.gcsUri;
      // console.log('[apiService] Successfully extracted GCS URI:', gcsUri);

      // 2. Download using the stream-audio endpoint with response ID and track ID
      const streamEndpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stream-audio/${ai_response_id}/${trackId}`;
      // console.log('[apiService] Downloading using stream endpoint:', streamEndpoint);
      
      const response = await fetch(streamEndpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        // Try to get error message from response if possible
        let errorBody = `Download failed with status: ${response.status}`;
        try {
            const errorText = await response.text(); // or response.json() if you expect JSON
            errorBody += ` - ${errorText}`;
        } catch (e) {
            // ignore if can't read body
            Sentry.captureException(e);
        }
        throw new Error(errorBody);
      }

      // Get the blob from the response
      const blob = await response.blob();
      // console.log('[apiService] Audio file downloaded, size:', blob.size);

      // Create a download link and trigger it
      // console.log('[apiService] Creating download link');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      let filename;
      const safeFileType = String(fileType || 'wav').toLowerCase();
      if (songTitle && String(songTitle).trim() !== '') {
        const sanitizedTitle = String(songTitle)
          .replace(/[\/\\:*?"<>|]/g, '') 
          .trim();
        
        filename = `${sanitizedTitle}.${safeFileType}`;
      } else {
        const originalFilename = gcsUri.split('/').pop() || `audio-${ai_response_id}-track${trackId}`;
        const baseFilename = originalFilename.substring(0, originalFilename.lastIndexOf('.')) || originalFilename;
        filename = `${baseFilename}.${safeFileType}`;
      }
      
      a.download = filename;
      
      document.body.appendChild(a);
      console.log('[apiService] Triggering download for file:', filename);
      a.click();
      
      console.log('[apiService] Cleaning up download resources');
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true, filename: filename }; // Return a success indicator
    } catch (error) {
      console.error("[apiService] Error in downloadAudio (v2):", {
        name: error.name,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      Sentry.captureException(error);
      throw error;
    }
  },
};

export default apiService;